import i18n from 'i18next';

const Intl = window.Intl ?? require('intl');

const {NumberFormat} = Intl;

export enum ECurrency {
  RUB = 'RUB',
  USD = 'USD',
  EUR = 'EUR',
  GBP = 'GBP',
  IDR = 'IDR',
  KZT = 'KZT',
  BYN = 'BYN',
}

export type TCurrency = keyof typeof ECurrency;

export enum ECurrencySymbol {
  RUB = '₽',
  USD = '$',
  EUR = '€',
  GBP = '£',
  IDR = 'Rp',
  KZT = '₸',
  BYN = 'Br',
}

const currenciesInfo = {
  [ECurrency.RUB]: {
    symbol: ECurrencySymbol.RUB,
    numberFormatOptions: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  [ECurrency.USD]: {
    symbol: ECurrencySymbol.USD,
    numberFormatOptions: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  [ECurrency.EUR]: {
    symbol: ECurrencySymbol.EUR,
    numberFormatOptions: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  [ECurrency.GBP]: {
    symbol: ECurrencySymbol.GBP,
    numberFormatOptions: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  [ECurrency.IDR]: {
    symbol: ECurrencySymbol.IDR,
    numberFormatOptions: {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
  },
  [ECurrency.KZT]: {
    symbol: ECurrencySymbol.KZT,
    numberFormatOptions: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  [ECurrency.BYN]: {
    symbol: ECurrencySymbol.BYN,
    numberFormatOptions: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
};

enum ECurrencyDisplay {
  Code = 'code',
  Name = 'name',
}

type TConfig = {
  minimumFractionDigits: number;
  maximumFractionDigits: number;
  style: string;
  currency: TCurrency;
  currencyDisplay: ECurrencyDisplay;
};

export const formatNumber = (
  value: number,
  config: Partial<TConfig> = {},
  language = i18n.language,
) => new NumberFormat(language, config).format(value);

type TFormatMoneyProps = {
  amount: number;
  useSymbol?: boolean;
  showCurrency?: boolean;
  currency?: TCurrency;
  fractionDigitsCount?: number;
  language?: string;
};

const formatMoney = ({
  amount,
  useSymbol = true,
  showCurrency = true,
  currency = ECurrency.RUB,
  fractionDigitsCount,
  language = i18n.language,
}: TFormatMoneyProps) => {
  const currencyInfo = currenciesInfo[currency];

  if (!currencyInfo) {
    return `${amount} ${currency}`;
  }
  const formatConfig = {
    currency,
    style: 'currency',
    currencyDisplay: useSymbol ? ECurrencyDisplay.Code : ECurrencyDisplay.Name,
    minimumFractionDigits:
      fractionDigitsCount ?? currencyInfo.numberFormatOptions.minimumFractionDigits,
    maximumFractionDigits:
      fractionDigitsCount ?? currencyInfo.numberFormatOptions.maximumFractionDigits,
  };

  let result = formatNumber(amount, formatConfig, language);

  if (useSymbol) {
    result = result.replace(currency, showCurrency ? currencyInfo.symbol : '');
  }

  return result.trim();
};

export default formatMoney;
